if (!customElements.get('hero-offer-selector')) {
  class HeroOfferSelector extends HTMLElement {
    connectedCallback() {
      this.brandField = this.querySelector('[data-field="brand"]');
      this.deviceField = this.querySelector('[data-field="device"]');
      this.cta = this.querySelector('.hero-offer__cta');
      this.brands = [...this.brandField.querySelectorAll('.hero-offer__panel > .hero-offer__option')];
      if (!this.brands.length) return;

      if (!this.fields) {
        this.fields = [this.brandField, this.deviceField];
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);
        const onFieldClick = this.onFieldClick.bind(this);
        const onFieldKeydown = this.onFieldKeydown.bind(this);
        this.fields.forEach((field) => {
          field.addEventListener('click', onFieldClick);
          field.addEventListener('keydown', onFieldKeydown);
        });
        this.selectBrand(this.brands[0]);
      }

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    disconnectedCallback() {
      if (!this.fields) return;
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    labelOf(option) {
      return option.querySelector('.hero-offer__option-label')?.textContent.trim() ?? '';
    }

    // Filter value for a device: "Label | value" lines set data-value; plain lines use the label.
    valueOf(option) {
      return option.dataset.value || this.labelOf(option);
    }

    optionsOf(field) {
      return [...field.querySelector('.hero-offer__panel').children].filter((el) => el.matches('.hero-offer__option'));
    }

    markSelected(field, selected) {
      this.optionsOf(field).forEach((option) => option.setAttribute('aria-selected', String(option === selected)));
      field.querySelector('.hero-offer__field-value').textContent = this.labelOf(selected);
    }

    selectBrand(brand) {
      this.brand = brand;
      this.markSelected(this.brandField, brand);

      const devicePanel = this.deviceField.querySelector('.hero-offer__panel');
      devicePanel.replaceChildren(brand.querySelector('template').content.cloneNode(true));
      const devices = this.optionsOf(this.deviceField);
      const preferred = brand.dataset.defaultDevice;
      this.selectDevice(devices.find((device) => this.valueOf(device) === preferred || this.labelOf(device) === preferred) ?? devices[0]);
    }

    selectDevice(device) {
      this.device = device ?? null;
      if (device) {
        this.markSelected(this.deviceField, device);
      } else {
        this.deviceField.querySelector('.hero-offer__field-value').textContent = '';
      }
      this.updateLink();
    }

    updateLink() {
      const url = this.brand?.dataset.url;
      if (!this.cta || !url) return;
      if (!this.device) {
        this.cta.href = url;
        return;
      }
      const value = encodeURIComponent(this.valueOf(this.device)).replace(/%20/g, '+');
      this.cta.href = `${url}?${this.dataset.filterParam}=${value}`;
    }

    open(field) {
      this.fields.forEach((other) => other !== field && this.close(other));
      const panel = field.querySelector('.hero-offer__panel');
      panel.hidden = false;
      field.classList.add('is-open');
      field.querySelector('.hero-offer__field-button').setAttribute('aria-expanded', 'true');
      const current = panel.querySelector('[aria-selected="true"]') ?? panel.querySelector('.hero-offer__option');
      current?.scrollIntoView({ block: 'nearest' });
      current?.focus({ preventScroll: true });
    }

    close(field, restoreFocus = false) {
      const panel = field.querySelector('.hero-offer__panel');
      if (panel.hidden) return;
      panel.hidden = true;
      field.classList.remove('is-open');
      const button = field.querySelector('.hero-offer__field-button');
      button.setAttribute('aria-expanded', 'false');
      if (restoreFocus) button.focus();
    }

    pick(field, option) {
      if (field === this.brandField) {
        this.selectBrand(option);
      } else {
        this.selectDevice(option);
      }
      this.close(field, true);
    }

    onFieldClick(event) {
      const field = event.currentTarget;
      const option = event.target.closest('.hero-offer__option');
      if (option) {
        this.pick(field, option);
        return;
      }
      if (!event.target.closest('.hero-offer__field-button')) return;
      if (field.classList.contains('is-open')) {
        this.close(field);
      } else {
        this.open(field);
      }
    }

    onFieldKeydown(event) {
      const field = event.currentTarget;
      const option = event.target.closest('.hero-offer__option');

      if (event.key === 'Escape') {
        this.close(field, true);
        return;
      }

      if (!option) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.open(field);
        }
        return;
      }

      const options = this.optionsOf(field);
      const index = options.indexOf(option);
      const moves = {
        ArrowDown: index + 1,
        ArrowUp: index - 1,
        Home: 0,
        End: options.length - 1,
      };

      if (event.key in moves) {
        event.preventDefault();
        options[Math.min(Math.max(moves[event.key], 0), options.length - 1)].focus();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.pick(field, option);
        return;
      }

      if (event.key === 'Tab') this.close(field);
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.fields.forEach((field) => this.close(field));
    }

    onBlockSelect(event) {
      if (this.brands.includes(event.target)) this.open(this.brandField);
    }

    onBlockDeselect(event) {
      if (this.brands.includes(event.target)) this.close(this.brandField);
    }
  }

  customElements.define('hero-offer-selector', HeroOfferSelector);
}
